import { Injectable } from '@angular/core';


@Injectable({ providedIn: 'root' })
export class DeviceKeysService {
  private static readonly DB_NAME = 'sso-device-keys';
  private static readonly STORE = 'keys';
  private static readonly ACTIVE_ID = 'device-keypair';
  private static readonly PENDING_ID = 'device-keypair-pending';

  private activePair: CryptoKeyPair | null = null;
  private pendingPair: CryptoKeyPair | null = null;


  async hasKeyPair(): Promise<boolean> {
    try {
      return !!(await this.loadActive());
    } catch {
      return false;
    }
  }

  async solveChallenge(challengeBase64: string): Promise<string> {
    const pair = await this.loadActive();
    if (!pair) throw new Error('Device keypair missing');
    return this.decryptWith(pair, challengeBase64);
  }


  async rotateAndGetPublicKeyPem(): Promise<string> {
    const pair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt'],
    );

    await this.idbPut(DeviceKeysService.PENDING_ID, pair);
    this.pendingPair = pair;

    const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
    return this.toPem(spki, 'PUBLIC KEY');
  }

  async solvePendingChallenge(challengeBase64: string): Promise<string> {
    const pair = await this.loadPending();
    if (!pair) throw new Error('Pending device keypair missing');
    return this.decryptWith(pair, challengeBase64);
  }

  async promotePendingKeyPair(): Promise<void> {
    const pending = await this.loadPending();
    if (!pending) return;
    await this.idbPut(DeviceKeysService.ACTIVE_ID, pending);
    await this.idbDelete(DeviceKeysService.PENDING_ID);
    this.activePair = pending;
    this.pendingPair = null;
  }


  async clear(): Promise<void> {
    this.activePair = null;
    this.pendingPair = null;
    try {
      await this.idbDelete(DeviceKeysService.ACTIVE_ID);
      await this.idbDelete(DeviceKeysService.PENDING_ID);
    } catch {
    }
  }

  private async decryptWith(pair: CryptoKeyPair, challengeBase64: string): Promise<string> {
    const binary = atob(challengeBase64.trim());
    const encrypted = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      encrypted[i] = binary.charCodeAt(i);
    }

    const decrypted = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      pair.privateKey,
      encrypted,
    );

    return new TextDecoder().decode(decrypted).trim();
  }

  private async loadActive(): Promise<CryptoKeyPair | null> {
    if (this.activePair) return this.activePair;
    const stored = await this.idbGetPair(DeviceKeysService.ACTIVE_ID);
    if (stored) this.activePair = stored;
    return stored;
  }

  private async loadPending(): Promise<CryptoKeyPair | null> {
    if (this.pendingPair) return this.pendingPair;
    const stored = await this.idbGetPair(DeviceKeysService.PENDING_ID);
    if (stored) this.pendingPair = stored;
    return stored;
  }

  private async idbGetPair(id: string): Promise<CryptoKeyPair | null> {
    try {
      const stored = (await this.idbGet(id)) as CryptoKeyPair | undefined;
      return stored?.privateKey && stored?.publicKey ? stored : null;
    } catch {
      return null;
    }
  }

  // ─────────────────────────── IndexedDB helpers ───────────────────────────

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DeviceKeysService.DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(DeviceKeysService.STORE)) {
          request.result.createObjectStore(DeviceKeysService.STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async idbGet(key: string): Promise<unknown> {
    const db = await this.openDb();
    try {
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(DeviceKeysService.STORE, 'readonly');
        const req = tx.objectStore(DeviceKeysService.STORE).get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    } finally {
      db.close();
    }
  }

  private async idbPut(key: string, value: unknown): Promise<void> {
    const db = await this.openDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(DeviceKeysService.STORE, 'readwrite');
        tx.objectStore(DeviceKeysService.STORE).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  }

  private async idbDelete(key: string): Promise<void> {
    const db = await this.openDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(DeviceKeysService.STORE, 'readwrite');
        tx.objectStore(DeviceKeysService.STORE).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  }

  private toPem(buffer: ArrayBuffer, label: string): string {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    const lines = base64.match(/.{1,64}/g) || [];
    return `-----BEGIN ${label}-----\n${lines.join('\n')}\n-----END ${label}-----`;
  }
}
